import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { dashboardAPI } from '../services/api'
import {
  BeakerIcon,
  HeartIcon,
  FireIcon,
  BoltIcon,
  SunIcon,
  MoonIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline'
import {
  AreaChart, Area, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine,
} from 'recharts'
import { useChartTheme } from '../hooks/useChartTheme'


export default function Dashboard() {
  // VH-133: recharts takes colours as props, so it inherits nothing from
  // the design system — these follow the active theme instead.
  const chart = useChartTheme()
  const { t } = useTranslation()
  const navigate = useNavigate()

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['dashboardSummary'],
    queryFn: async () => {
      const res = await dashboardAPI.getSummary()
      return res.data
    },
    staleTime: 5 * 60 * 1000,
  })

  // ── Loading skeleton ─────────────────────────────────────────────
  if (isLoading) {
    return (
      <div className="p-6">
        <div className="mb-8">
          <div className="h-8 w-48 bg-gray-200 rounded animate-pulse" />
          <div className="h-4 w-64 bg-gray-200 rounded animate-pulse mt-2" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="card">
              <div className="card-body">
                <div className="h-16 bg-gray-200 rounded animate-pulse" />
              </div>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {[...Array(2)].map((_, i) => (
            <div key={i} className="card">
              <div className="card-body">
                <div className="h-48 bg-gray-200 rounded animate-pulse" />
              </div>
            </div>
          ))}
        </div>
      </div>
    )
  }

  // ── Error state ──────────────────────────────────────────────────
  if (isError || !data) {
    return (
      <div className="p-6 text-center py-20">
        <ExclamationTriangleIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <p className="text-gray-600 mb-4">{t('dashboard.errorLoading')}</p>
        <button onClick={() => refetch()} className="btn-primary inline-flex items-center">
          <ArrowPathIcon className="h-4 w-4 mr-2" />
          {t('dashboard.retry')}
        </button>
      </div>
    )
  }

  const { stat_cards: sc, nutrition_trend, body_weight_trend, blood_test_status, supplement_schedule, workout_progress, macro_distribution } = data

  // ── Helpers ──────────────────────────────────────────────────────
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString() : '—'
  const calPct = sc.today_calories_goal
    ? Math.min(Math.round((sc.today_calories / sc.today_calories_goal) * 100), 100)
    : null

  const macroData = [
    { name: t('dashboard.proteinLabel'), value: macro_distribution.protein_g },
    { name: t('dashboard.carbsLabel'), value: macro_distribution.carbs_g },
    { name: t('dashboard.fatLabel'), value: macro_distribution.fat_g },
  ].filter(d => d.value > 0)

  // ── Render ───────────────────────────────────────────────────────
  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">{t('dashboard.title')}</h1>
        <p className="mt-1 text-gray-500">{t('dashboard.subtitle')}</p>
      </div>

      {/* ─── Row 1: Stat Cards ─────────────────────────────────── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
        {/* Blood Tests */}
        <div className="card cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/blood-tests')}>
          <div className="card-body">
            <div className="flex items-center">
              <div className="p-3 rounded-md bg-green-100">
                <BeakerIcon className="h-6 w-6 text-green-600" />
              </div>
              <div className="ml-4 flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-500 truncate">{t('dashboard.bloodTests')}</p>
                <p className="text-2xl font-semibold text-gray-900">{sc.blood_test_count}</p>
                {sc.last_blood_test_date && (
                  <p className="text-xs text-gray-400">{t('dashboard.lastTest')}: {fmtDate(sc.last_blood_test_date)}</p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Active Supplements */}
        <div className="card cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/supplements')}>
          <div className="card-body">
            <div className="flex items-center">
              <div className="p-3 rounded-md bg-purple-100">
                <HeartIcon className="h-6 w-6 text-purple-600" />
              </div>
              <div className="ml-4 flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-500 truncate">{t('dashboard.activeSupplements')}</p>
                <p className="text-2xl font-semibold text-gray-900">{sc.active_supplement_count}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Today's Calories */}
        <div className="card cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/nutrition')}>
          <div className="card-body">
            <div className="flex items-center">
              <div className="p-3 rounded-md bg-orange-100">
                <FireIcon className="h-6 w-6 text-orange-600" />
              </div>
              <div className="ml-4 flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-500 truncate">{t('dashboard.todayCalories')}</p>
                <p className="text-2xl font-semibold text-gray-900">
                  {Math.round(sc.today_calories)} <span className="text-sm font-normal text-gray-400">{t('dashboard.kcal')}</span>
                </p>
                {calPct !== null ? (
                  <div className="mt-1 w-full bg-gray-200 rounded-full h-1.5">
                    <div
                      className={`h-1.5 rounded-full ${calPct >= 100 ? 'bg-green-500' : 'bg-orange-400'}`}
                      style={{ width: `${calPct}%` }}
                    />
                  </div>
                ) : (
                  <p className="text-xs text-gray-400">{t('dashboard.noGoalSet')}</p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Weekly Workouts */}
        <div className="card cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/programs')}>
          <div className="card-body">
            <div className="flex items-center">
              <div className="p-3 rounded-md bg-blue-100">
                <BoltIcon className="h-6 w-6 text-blue-600" />
              </div>
              <div className="ml-4 flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-500 truncate">{t('dashboard.weeklyWorkouts')}</p>
                <p className="text-2xl font-semibold text-gray-900">
                  {sc.weekly_workout_sessions_completed} <span className="text-sm font-normal text-gray-400">/ {sc.weekly_workout_sessions_total}</span>
                </p>
                <p className="text-xs text-gray-400">{t('dashboard.sessions')}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ─── Row 2: Charts ─────────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Nutrition Trend (7d) */}
        <div className="card">
          <div className="card-header flex justify-between items-center">
            <h3 className="text-lg font-medium text-gray-900">{t('dashboard.nutritionTrend')}</h3>
            <div className="flex gap-4 text-xs text-gray-500">
              <span>{t('dashboard.avgCalories')}: <strong>{Math.round(nutrition_trend.avg_calories)}</strong></span>
              <span>{t('dashboard.avgProtein')}: <strong>{Math.round(nutrition_trend.avg_protein_g)}g</strong></span>
            </div>
          </div>
          <div className="card-body">
            {nutrition_trend.days.some(d => d.calories > 0) ? (
              <ResponsiveContainer width="100%" height={220}>
                <AreaChart data={nutrition_trend.days} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={chart.grid} vertical={false} />
                  <XAxis dataKey="day_label" tick={{ fontSize: 12, fill: chart.axis }} />
                  <YAxis tick={{ fontSize: 12, fill: chart.axis }} />
                  <Tooltip {...chart.tooltipProps} />
                  {/* VH-133: the fill is the stroke at low opacity rather than
                      a pale tint. `#fef3c7` and `#e0e7ff` were picked against a
                      white card and turn muddy khaki on a dark one; a
                      translucent wash of the line's own colour reads correctly
                      on either surface and keeps the two series distinct. */}
                  {/* VH-135: these were literal amber/indigo. A translucent
                      amber fill over the purple canvas reads as brown mud, so
                      the series take their colours from the palette. */}
                  <Area type="monotone" dataKey="calories" stroke={chart.accent} fill={chart.accent} fillOpacity={0.18} name={t('dashboard.calories')} />
                  <Area type="monotone" dataKey="protein_g" stroke={chart.protein} fill={chart.protein} fillOpacity={0.18} name={t('dashboard.protein')} />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <EmptyState
                message={t('dashboard.noNutritionData')}
                cta={t('dashboard.goToNutrition')}
                onClick={() => navigate('/nutrition')}
              />
            )}
          </div>
        </div>

        {/* Body Weight (30d) */}
        <div className="card">
          <div className="card-header flex justify-between items-center">
            <h3 className="text-lg font-medium text-gray-900">{t('dashboard.bodyWeight')}</h3>
            {body_weight_trend.weight_change_30d != null && (
              <span className={`inline-flex items-center text-sm font-medium ${body_weight_trend.weight_change_30d < 0 ? 'text-green-600' : body_weight_trend.weight_change_30d > 0 ? 'text-red-500' : 'text-gray-500'}`}>
                {body_weight_trend.weight_change_30d < 0 ? (
                  <ArrowTrendingDownIcon className="h-4 w-4 mr-1" />
                ) : (
                  <ArrowTrendingUpIcon className="h-4 w-4 mr-1" />
                )}
                {body_weight_trend.weight_change_30d > 0 ? '+' : ''}{body_weight_trend.weight_change_30d} kg
              </span>
            )}
          </div>
          <div className="card-body">
            {body_weight_trend.data_points.length > 0 ? (
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={body_weight_trend.data_points} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={chart.grid} vertical={false} />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 12, fill: chart.axis }}
                    tickFormatter={(v) => new Date(v).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })}
                  />
                  <YAxis domain={['dataMin - 1', 'dataMax + 1']} tick={{ fontSize: 12, fill: chart.axis }} />
                  <Tooltip labelFormatter={(v) => new Date(v).toLocaleDateString()} />
                  <Line type="monotone" dataKey="weight_kg" stroke={chart.primary} strokeWidth={2} dot={{ r: 3 }} />
                  {body_weight_trend.target_weight_kg && (
                    <ReferenceLine y={body_weight_trend.target_weight_kg} stroke={chart.success} strokeDasharray="4 4" label={t('dashboard.target')} />
                  )}
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <EmptyState
                message={t('dashboard.noWeightData')}
                cta={t('dashboard.goToProfile')}
                onClick={() => navigate('/profile')}
              />
            )}
          </div>
        </div>
      </div>

      {/* ─── Row 3: Detail Widgets ─────────────────────────────── */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* Blood Test Status */}
        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-medium text-gray-900">{t('dashboard.bloodTestStatus')}</h3>
          </div>
          <div className="card-body">
            {blood_test_status.total_tests > 0 ? (
              <div className="space-y-3">
                {blood_test_status.health_score != null && (
                  <div className="text-center mb-2">
                    <span className={`text-3xl font-bold ${
                      blood_test_status.health_score >= 80 ? 'text-green-600' :
                      blood_test_status.health_score >= 60 ? 'text-yellow-500' : 'text-red-500'
                    }`}>
                      {blood_test_status.health_score}
                    </span>
                    <p className="text-xs text-gray-500">{t('dashboard.healthScore')}</p>
                  </div>
                )}
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">{t('dashboard.totalTests')}</span>
                  <span className="font-medium">{blood_test_status.total_tests}</span>
                </div>
                {blood_test_status.latest_test_lab && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">{t('dashboard.latestLab')}</span>
                    <span className="font-medium">{blood_test_status.latest_test_lab}</span>
                  </div>
                )}
                <div className="flex gap-2 mt-2">
                  {blood_test_status.abnormal_markers_count > 0 && (
                    <span className="badge badge-warning">{blood_test_status.abnormal_markers_count} {t('dashboard.abnormalMarkers')}</span>
                  )}
                  {blood_test_status.critical_markers_count > 0 && (
                    <span className="badge badge-danger">{blood_test_status.critical_markers_count} {t('dashboard.criticalMarkers')}</span>
                  )}
                </div>
              </div>
            ) : (
              <EmptyState
                message={t('dashboard.noBloodTests')}
                cta={t('dashboard.uploadBloodTest')}
                onClick={() => navigate('/blood-tests')}
              />
            )}
          </div>
        </div>

        {/* Today's Supplements */}
        <div className="card">
          <div className="card-header flex justify-between items-center">
            <h3 className="text-lg font-medium text-gray-900">{t('dashboard.todaysSupplements')}</h3>
            {supplement_schedule.total_doses > 0 && (
              <span className="badge badge-gray">{supplement_schedule.total_doses} {t('dashboard.doses')}</span>
            )}
          </div>
          <div className="card-body">
            {supplement_schedule.total_doses > 0 ? (
              <div className="space-y-3">
                <TimeSlot icon={SunIcon} label={t('dashboard.morning')} items={supplement_schedule.morning} color="text-amber-500" />
                <TimeSlot icon={FireIcon} label={t('dashboard.midday')} items={supplement_schedule.midday} color="text-orange-500" />
                <TimeSlot icon={MoonIcon} label={t('dashboard.evening')} items={supplement_schedule.evening} color="text-indigo-500" />
              </div>
            ) : (
              <EmptyState
                message={t('dashboard.noSupplements')}
                cta={t('dashboard.addSupplement')}
                onClick={() => navigate('/supplements')}
              />
            )}
          </div>
        </div>

        {/* Workout Progress + Today's Macros */}
        <div className="space-y-6">
          {/* Workout Progress */}
          <div className="card">
            <div className="card-header">
              <h3 className="text-lg font-medium text-gray-900">{t('dashboard.workoutProgress')}</h3>
            </div>
            <div className="card-body">
              {workout_progress.sessions_total > 0 ? (
                <div>
                  <div className="flex items-end justify-between mb-2">
                    <span className="text-3xl font-bold text-gray-900">{workout_progress.sessions_completed}<span className="text-base font-normal text-gray-400">/{workout_progress.sessions_total}</span></span>
                    <span className="text-sm text-gray-500">{Math.round(workout_progress.completion_percentage)}% {t('dashboard.completed')}</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2.5">
                    <div
                      className="bg-blue-600 h-2.5 rounded-full transition-all"
                      style={{ width: `${workout_progress.completion_percentage}%` }}
                    />
                  </div>
                  {workout_progress.program_name && (
                    <p className="text-xs text-gray-400 mt-2">{workout_progress.program_name}</p>
                  )}
                </div>
              ) : (
                <EmptyState
                  message={t('dashboard.noProgram')}
                  cta={t('dashboard.createProgram')}
                  onClick={() => navigate('/programs')}
                />
              )}
            </div>
          </div>

          {/* Today's Macros */}
          <div className="card">
            <div className="card-header">
              <h3 className="text-lg font-medium text-gray-900">{t('dashboard.todaysMacros')}</h3>
            </div>
            <div className="card-body">
              {macroData.length > 0 ? (
                <div className="flex items-center gap-4">
                  <div className="w-28 h-28 flex-shrink-0">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={macroData}
                          cx="50%"
                          cy="50%"
                          innerRadius={28}
                          outerRadius={50}
                          dataKey="value"
                          stroke="none"
                        >
                          {macroData.map((_, idx) => (
                            <Cell key={idx} fill={chart.macros[idx]} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(v) => `${Math.round(v)}g`} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="space-y-1 text-sm flex-1">
                    {macroData.map((d, idx) => (
                      <div key={d.name} className="flex items-center justify-between">
                        <div className="flex items-center">
                          <span className="w-2.5 h-2.5 rounded-full mr-2" style={{ backgroundColor: chart.macros[idx] }} />
                          <span className="text-gray-600">{d.name}</span>
                        </div>
                        <span className="font-medium">{Math.round(d.value)}g</span>
                      </div>
                    ))}
                    <div className="border-t pt-1 mt-1 flex justify-between">
                      <span className="text-gray-500">{t('dashboard.todayCalories')}</span>
                      <span className="font-semibold">{Math.round(macro_distribution.total_calories)} {t('dashboard.kcal')}</span>
                    </div>
                  </div>
                </div>
              ) : (
                <EmptyState
                  message={t('dashboard.noMeals')}
                  cta={t('dashboard.logMeal')}
                  onClick={() => navigate('/nutrition')}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

// ── Shared sub-components ────────────────────────────────────────

function EmptyState({ message, cta, onClick }) {
  return (
    <div className="text-center py-6">
      <p className="text-sm text-gray-500 mb-3">{message}</p>
      <button onClick={onClick} className="btn-primary text-sm">{cta}</button>
    </div>
  )
}

function TimeSlot({ icon: Icon, label, items, color }) {
  if (!items || items.length === 0) return null
  return (
    <div>
      <div className="flex items-center mb-1">
        <Icon className={`h-4 w-4 ${color} mr-1.5`} />
        <span className="text-sm font-medium text-gray-700">{label}</span>
      </div>
      <div className="ml-6 space-y-0.5">
        {items.map((item) => (
          <div key={`${item.id}-${label}`} className="flex justify-between text-sm">
            <span className="text-gray-600">{item.name}</span>
            <span className="text-gray-400">
              {item.quantity}{item.unit ? ` ${item.unit}` : ''}
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}
